import { FormSelectAsyncPaginate } from '@common/FormSelectAsyncPaginate';
import { useFetchCompanyDistributionOptions } from '@company/projectCompany/projectCompany.queries';
import { useProjectId } from '@config/config.hooks';
import {
  appendClassNameByCondition,
  Checkbox,
  CheckboxType,
  Form,
  FormSelect,
  Grid,
  Label,
  requiredRule,
  UseFormReturn,
} from '@gcl/react-core';
import { useFetchTeamDistributionOptionsWithSearch } from '@projectTeam/projectTeam.queries';
import { useFetchRoleDistributionOptions } from '@role/projectRole/projectRole.queries';
import { useFetchUserDistributionOptionsWithSearch } from '@user/user.queries';
import * as React from 'react';
import { transmittalDistributionFormKeys } from './transmittalDetailPage.constants';
import './TransmittalDetailDistributionForm.scss';

interface ITransmittalDetailDistributionFormProps {
  useFormMethods: UseFormReturn;
  masterScheduleId: number;
}
export const TransmittalDetailDistributionForm: React.FunctionComponent<ITransmittalDetailDistributionFormProps> = ({
  useFormMethods,
  masterScheduleId,
}) => {
  const projectId = useProjectId();

  const selectedCompany = useFormMethods.watch(transmittalDistributionFormKeys.GROUP);
  const selectedRole = useFormMethods.watch(transmittalDistributionFormKeys.USER_ROLE);

  const { data: companyDistributionOptionData } = useFetchCompanyDistributionOptions({})({
    projectId,
    masterScheduleId,
  });

  const { data: roleDistributionOptionData } = useFetchRoleDistributionOptions({})({
    companyTypeId: selectedCompany?.data?.companyTypeId,
    organizationTypeId: selectedCompany?.data?.organizationTypeId,
    projectId,
  });

  React.useEffect(() => {
    if (!useFormMethods.getFieldState(transmittalDistributionFormKeys.GROUP).isDirty) return;
    useFormMethods.resetField(transmittalDistributionFormKeys.USER_ROLE);
    useFormMethods.resetField(transmittalDistributionFormKeys.TEAM);
    useFormMethods.resetField(transmittalDistributionFormKeys.INDIVIDUAL_USER);
  }, [JSON.stringify(selectedCompany)]);

  const distributeToKeys = {
    ROLE: 'role',
    TEAM: 'team',
    USER: 'user',
  };

  const [selectedDistributeTo, setSelectedDistributeTo] = React.useState(distributeToKeys.ROLE);

  const onChangeDistributeTo = (distributeToKey: string) => {
    useFormMethods.clearErrors();
    switch (distributeToKey) {
      case distributeToKeys.ROLE:
        useFormMethods.resetField(transmittalDistributionFormKeys.TEAM);
        useFormMethods.resetField(transmittalDistributionFormKeys.INDIVIDUAL_USER);
        break;
      case distributeToKeys.TEAM:
        useFormMethods.resetField(transmittalDistributionFormKeys.USER_ROLE);
        useFormMethods.resetField(transmittalDistributionFormKeys.INDIVIDUAL_USER);
        break;

      case distributeToKeys.USER:
        useFormMethods.resetField(transmittalDistributionFormKeys.TEAM);
        useFormMethods.resetField(transmittalDistributionFormKeys.TEAM);
        break;

      default:
        break;
    }
    setSelectedDistributeTo(distributeToKey);
  };

  return (
    <Form
      useFormMethods={useFormMethods}
      render={(formFieldMethods) => (
        <div>
          <Label labelFor={transmittalDistributionFormKeys.GROUP} required>
            Company
          </Label>
          <FormSelect
            rules={requiredRule}
            displayFieldName="text"
            placeholder="Select company"
            formFieldMethods={formFieldMethods}
            options={companyDistributionOptionData ?? []}
            id={transmittalDistributionFormKeys.GROUP}
          />

          <Label labelFor="distributeTo" required>
            Distribute to one of below:
          </Label>
          <Grid container columnSpacing={1}>
            <Grid item xs={4} className="gcl-gems-distribution-grid">
              <Checkbox
                id="distribution-role"
                className="gcl-gems-distribution-grid-overlay-selector-radio"
                onChange={() => {
                  onChangeDistributeTo(distributeToKeys.ROLE);
                }}
                type={CheckboxType.Radio}
                checked={selectedDistributeTo === distributeToKeys.ROLE}
              />
              <div
                onClick={() => {
                  onChangeDistributeTo(distributeToKeys.ROLE);
                }}
                className={appendClassNameByCondition(
                  selectedDistributeTo === distributeToKeys.ROLE,
                  'gcl-gems-distribution-grid-overlay-selector',
                  'gcl-gems-distribution-grid-overlay-selector-selected',
                )}
              />
              <Label labelFor={transmittalDistributionFormKeys.USER_ROLE} className="gcl-px-xs">
                Role
              </Label>
              <FormSelect
                rules={{
                  validate: {
                    required: (value: any) => {
                      if (selectedDistributeTo === distributeToKeys.ROLE && !value) return 'This field is required';
                      return true;
                    },
                  },
                }}
                className="gcl-px-xs gcl-pb-xs"
                multiple
                displayFieldName="text"
                placeholder="Select user role"
                formFieldMethods={formFieldMethods}
                options={roleDistributionOptionData ?? []}
                id={transmittalDistributionFormKeys.USER_ROLE}
                disabled={!selectedCompany || selectedCompany === null}
              />
            </Grid>

            <Grid item xs={4} className="gcl-gems-distribution-grid">
              <Checkbox
                id="distribution-team"
                className="gcl-gems-distribution-grid-overlay-selector-radio"
                onChange={() => {
                  onChangeDistributeTo(distributeToKeys.TEAM);
                }}
                type={CheckboxType.Radio}
                checked={selectedDistributeTo === distributeToKeys.TEAM}
              />
              <div
                onClick={() => {
                  onChangeDistributeTo(distributeToKeys.TEAM);
                }}
                className={appendClassNameByCondition(
                  selectedDistributeTo === distributeToKeys.TEAM,
                  'gcl-gems-distribution-grid-overlay-selector',
                  'gcl-gems-distribution-grid-overlay-selector-selected',
                )}
              />
              <Label labelFor={transmittalDistributionFormKeys.TEAM} className="gcl-px-xs">
                Team
              </Label>
              <FormSelectAsyncPaginate
                rules={{
                  validate: {
                    required: (value: any) => {
                      if (selectedDistributeTo === distributeToKeys.TEAM && !value) return 'This field is required';
                      return true;
                    },
                  },
                }}
                className="gcl-px-xs  gcl-pb-xs"
                multiple
                shouldDebounce
                isPaginate={false}
                placeholder="Select team"
                displayFieldName="teamName"
                formFieldMethods={formFieldMethods}
                useFetchOptions={useFetchTeamDistributionOptionsWithSearch({ shouldConvertOptions: false })}
                useFetchOptionsProps={{
                  projectId,
                  masterScheduleId,
                  projectCompanyId: selectedCompany?.data?.id,
                }}
                id={transmittalDistributionFormKeys.TEAM}
                disabled={!selectedCompany || selectedCompany === null}
              />
            </Grid>

            <Grid item xs={4} className="gcl-gems-distribution-grid">
              <Checkbox
                id="distribution-user"
                className="gcl-gems-distribution-grid-overlay-selector-radio"
                onChange={() => {
                  onChangeDistributeTo(distributeToKeys.USER);
                }}
                type={CheckboxType.Radio}
                checked={selectedDistributeTo === distributeToKeys.USER}
              />
              <div
                onClick={() => {
                  onChangeDistributeTo(distributeToKeys.USER);
                }}
                className={appendClassNameByCondition(
                  selectedDistributeTo === distributeToKeys.USER,
                  'gcl-gems-distribution-grid-overlay-selector',
                  'gcl-gems-distribution-grid-overlay-selector-selected',
                )}
              />
              <Label labelFor={transmittalDistributionFormKeys.INDIVIDUAL_USER} className="gcl-px-xs">
                Individual user
              </Label>
              <FormSelectAsyncPaginate
                rules={{
                  validate: {
                    required: (value: any) => {
                      if (selectedDistributeTo === distributeToKeys.USER && !value) return 'This field is required';
                      return true;
                    },
                  },
                }}
                className="gcl-px-xs gcl-pb-xs"
                multiple
                shouldDebounce
                placeholder="Select individual user"
                displayFieldName="displayName"
                formFieldMethods={formFieldMethods}
                useFetchOptions={useFetchUserDistributionOptionsWithSearch({ shouldConvertOptions: false })}
                useFetchOptionsProps={{
                  projectId,
                  masterScheduleId,
                  projectCompanyId: selectedCompany?.data?.id,
                }}
                id={transmittalDistributionFormKeys.INDIVIDUAL_USER}
                disabled={!selectedCompany || selectedCompany === null}
              />
            </Grid>
          </Grid>

          <Label labelFor="autoCopyDesc">{`Operations Secretary and Designer's Secretary are copied automatically`}</Label>
        </div>
      )}
    />
  );
};
